import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoneyKeepSubunits } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { hasSalesUnit } from '../../../util/salesUnit';
import { DELIVERY_PICKUP, DELIVERY_SHIPPING } from '../../../util/listingOrderSummary';
import { trackListingEvent } from '../../../util/ecommerceEvents';

import css from './MobileStickyCTA.module.css';

const { Money } = sdkTypes;

const DELIVERY_WORD = {
  [DELIVERY_SHIPPING]: 'frakt',
  [DELIVERY_PICKUP]: 'upphämtning',
};

/**
 * Phone sticky bar (P25009-482 BR-9 / AC-5).
 *
 * Shows the same order the buy card builds — the TOTAL for the chosen
 * quantity and delivery, and one line saying what it covers
 * ("4 hjul · inkl. moms och frakt") — plus the chat icon and Köp nu. It is
 * visible from the first screen: the buy card now sits directly under the
 * gallery, and AC-5 wants the total and Köp nu there without scrolling.
 *
 * Hidden from --viewportLarge up, where the desktop buy box shows.
 */
const MobileStickyCTA = ({
  listing,
  intl,
  isOwnListing,
  onBuyClick,
  onMessageClick,
  buyLabelId,
  isInquiryProcess,
  sellerCannotTakePayment,
  order,
}) => {
  const price = listing?.attributes?.price;
  const publicData = listing?.attributes?.publicData || {};
  const summary = order?.summary || null;
  const currency = price?.currency;

  /* A zero-stock listing cannot be bought, and this bar is a second buy
   * surface. `=== 0`, not falsy — a listing that never tracked stock has
   * `undefined` here and must stay buyable. */
  const isSoldOut = listing?.currentStock?.attributes?.quantity === 0;

  if (!price) return null;

  const showsOrder =
    !!summary && hasSalesUnit(publicData) && !isInquiryProcess && !sellerCannotTakePayment && !isSoldOut;
  const amount = showsOrder ? summary.totalSubunits : price.amount;
  const formattedAmount = formatMoneyKeepSubunits(intl, new Money(amount, currency));

  const noun = summary?.noun;
  const deliveryWord = order?.option ? DELIVERY_WORD[order.option.key] : null;
  /* "inkl. moms, frakt och miljöavgift" — everything the total holds besides
   * the goods (P25009-484 adds montering and the miljöavgift). */
  const included = [
    summary?.hasVat ? 'moms' : null,
    deliveryWord,
    summary?.servicesSubunits > 0 ? 'montering' : null,
    summary?.miljoavgiftSubunits > 0 ? 'miljöavgift' : null,
  ].filter(Boolean);
  const includedText =
    included.length > 1
      ? `inkl. ${included.slice(0, -1).join(', ')} och ${included[included.length - 1]}`
      : included.length === 1
        ? `inkl. ${included[0]}`
        : null;
  const includes = showsOrder
    ? [`${summary.wheels} ${summary.wheels === 1 ? noun?.one : noun?.many}`, includedText]
        .filter(Boolean)
        .join(' · ')
    : null;

  const handleMessage = () => {
    trackListingEvent('message_seller', { listing_id: listing?.id?.uuid, surface: 'sticky_bar' });
    onMessageClick();
  };

  return (
    <div className={classNames(css.root, css.visible)} role="region" aria-label="Köp">
      <div className={css.inner}>
        <div className={css.priceBlock}>
          <div className={css.price}>{formattedAmount}</div>
          {includes ? <div className={css.includes}>{includes}</div> : null}
        </div>
        {sellerCannotTakePayment ? (
          /* P25009-452 BR-1 — a Stripe-pending seller's ad must not show
           * "Köp nu" here either; contacting the seller is the one action. */
          <div className={css.buttons}>
            <button type="button" className={css.buyBtn} onClick={onMessageClick} disabled={isOwnListing}>
              <FormattedMessage id="ListingPage.contactSellerButton" defaultMessage="Kontakta säljaren" />
            </button>
          </div>
        ) : (
          <div className={css.buttons}>
            {!isInquiryProcess && publicData.allowMessages !== false ? (
              <button
                type="button"
                className={css.messageBtn}
                onClick={handleMessage}
                disabled={isOwnListing}
                aria-label="Skicka meddelande"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </button>
            ) : null}
            <button
              type="button"
              className={css.buyBtn}
              onClick={() => onBuyClick(order?.orderData || {})}
              disabled={isOwnListing || isSoldOut}
            >
              {isSoldOut ? (
                'Slutsåld'
              ) : (
                <FormattedMessage id={buyLabelId || 'ListingPage.orderTitle'} defaultMessage="Köp nu" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileStickyCTA;
